import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ManualInput = ({ onSubmitCredential }) => {
  const [inputMethod, setInputMethod] = useState('json');
  const [credentialData, setCredentialData] = useState('');
  const [credentialUrl, setCredentialUrl] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const inputMethodOptions = [
    { value: 'json', label: 'JSON Credential', description: 'Paste credential JSON data' },
    { value: 'url', label: 'Credential URL', description: 'Enter credential verification URL' },
    { value: 'id', label: 'Credential ID', description: 'Enter credential identifier' }
  ];

  const mockCredentialExamples = {
    json: `{
  "@context": ["https://www.w3.org/2018/credentials/v1"],
  "type": ["VerifiableCredential", "EducationalCredential"],
  "issuer": "did:example:university123",
  "issuanceDate": "2024-01-15T10:00:00Z",
  "credentialSubject": {
    "id": "did:example:student456",
    "degree": "Master of Science",
    "field": "Computer Science"
  },
  "proof": {
    "type": "Ed25519Signature2020",
    "created": "2024-01-15T10:00:00Z",
    "verificationMethod": "did:example:university123#key-1"
  }
}`,
    url: 'https://credentials.university.edu/verify/abc123def456',
    id: 'cred_edu_2024_001_stanford_cs_masters'
  };

  const validateInput = () => {
    const newErrors = {};

    switch (inputMethod) {
      case 'json':
        if (!credentialData?.trim()) {
          newErrors.credentialData = 'Credential JSON is required';
        } else {
          try {
            JSON.parse(credentialData);
          } catch (e) {
            newErrors.credentialData = 'Invalid JSON format';
          }
        }
        break;
      case 'url':
        if (!credentialUrl?.trim()) {
          newErrors.credentialUrl = 'Credential URL is required';
        } else if (!/^https?:\/\/.+/?.test(credentialUrl)) {
          newErrors.credentialUrl = 'Please enter a valid URL';
        }
        break;
      case 'id':
        if (!credentialId?.trim()) {
          newErrors.credentialId = 'Credential ID is required';
        } else if (credentialId?.length < 10) {
          newErrors.credentialId = 'Credential ID must be at least 10 characters';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateInput()) return;

    setIsSubmitting(true);

    try {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      let credentialPayload;

      switch (inputMethod) {
        case 'json':
          credentialPayload = {
            type: 'manual_json',
            credential: JSON.parse(credentialData),
            inputMethod: 'json',
            submissionTimestamp: new Date()?.toISOString()
          };
          break;
        case 'url':
          credentialPayload = {
            type: 'manual_url',
            credential: {
              id: "cred_url_" + Date.now(),
              type: "VerifiableCredential",
              issuer: "External Issuer",
              subject: "URL-based Credential",
              sourceUrl: credentialUrl,
              issuanceDate: "2024-01-15",
              expirationDate: "2025-01-15"
            },
            inputMethod: 'url',
            sourceUrl: credentialUrl,
            submissionTimestamp: new Date()?.toISOString()
          };
          break;
        case 'id':
          credentialPayload = {
            type: 'manual_id',
            credential: {
              id: credentialId,
              type: "VerifiableCredential",
              issuer: "Registry Lookup",
              subject: "ID-based Credential",
              issuanceDate: "2024-01-15",
              expirationDate: "2025-01-15",
              credentialSubject: {
                lookupId: credentialId,
                status: "active"
              }
            },
            inputMethod: 'id',
            credentialId: credentialId,
            submissionTimestamp: new Date()?.toISOString()
          };
          break;
      }

      onSubmitCredential(credentialPayload);
      
      // Reset form
      setCredentialData('');
      setCredentialUrl('');
      setCredentialId('');
      setErrors({});

    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Failed to submit credential. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseExample = () => {
    switch (inputMethod) {
      case 'json':
        setCredentialData(mockCredentialExamples?.json);
        break;
      case 'url':
        setCredentialUrl(mockCredentialExamples?.url);
        break;
      case 'id':
        setCredentialId(mockCredentialExamples?.id);
        break;
    }
    setErrors({});
  };

  const handleClear = () => {
    setCredentialData('');
    setCredentialUrl('');
    setCredentialId('');
    setErrors({});
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon name="FileText" size={20} className="text-primary mr-2" />
            <h3 className="font-semibold text-foreground">Manual Input</h3>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUseExample}
              iconName="Lightbulb"
              iconPosition="left"
            >
              Use Example
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
      {/* Input Method Selection */}
      <div className="p-4 border-b border-border bg-muted/30">
        <Select
          label="Input Method"
          description="Choose how you want to provide the credential data"
          options={inputMethodOptions}
          value={inputMethod}
          onChange={setInputMethod}
          className="mb-0"
        />
      </div>
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="p-4">
        {inputMethod === 'json' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Credential JSON Data
              </label>
              <textarea
                value={credentialData}
                onChange={(e) => setCredentialData(e?.target?.value)}
                placeholder="Paste your verifiable credential JSON here..."
                className="w-full h-64 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none font-mono text-sm"
                disabled={isSubmitting}
              />
              {errors?.credentialData && (
                <p className="mt-1 text-sm text-destructive">{errors?.credentialData}</p>
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Info" size={16} />
              <span>Supports JSON-LD, JWT, and standard verifiable credential formats</span>
            </div>
          </div>
        )}

        {inputMethod === 'url' && (
          <div className="space-y-4">
            <Input
              label="Credential Verification URL"
              type="url"
              placeholder="https://example.com/credentials/verify/..."
              value={credentialUrl}
              onChange={(e) => setCredentialUrl(e?.target?.value)}
              error={errors?.credentialUrl}
              description="Enter the URL where the credential can be verified"
              disabled={isSubmitting}
            />
            
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Globe" size={16} />
              <span>URL will be accessed to retrieve and verify the credential</span>
            </div>
          </div>
        )}

        {inputMethod === 'id' && (
          <div className="space-y-4">
            <Input
              label="Credential Identifier"
              type="text"
              placeholder="cred_edu_2024_001_university_degree"
              value={credentialId}
              onChange={(e) => setCredentialId(e?.target?.value)}
              error={errors?.credentialId}
              description="Enter the unique identifier for the credential"
              disabled={isSubmitting}
            />
            
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Hash" size={16} />
              <span>ID will be used to lookup the credential in registries</span>
            </div>
          </div>
        )}

        {/* Submit Error */}
        {errors?.submit && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={16} className="text-destructive mr-2" />
              <span className="text-sm text-destructive">{errors?.submit}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            disabled={isSubmitting}
            iconName="Shield"
            iconPosition="left"
          >
            {isSubmitting ? 'Verifying...' : 'Verify Credential'}
          </Button>
        </div>
      </form>
      {/* Help Section */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-start space-x-3">
          <Icon name="HelpCircle" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-2">Supported Formats:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">JSON-LD</p>
                <p>W3C Verifiable Credentials standard format with linked data context</p>
              </div>
              <div>
                <p className="font-medium text-foreground">JWT</p>
                <p>JSON Web Token format with embedded credential claims</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Custom</p>
                <p>Institution-specific formats with standard verification methods</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualInput;